import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { LanguageProvider } from './contexts/LanguageContext';
import { AuthProvider, useAuth } from './contexts/AuthContext';

// Layout Components
import Navbar from './components/Navbar';
import Footer from './components/Footer';

// Public Pages
import Home from './pages/Home';
import Services from './pages/Services';
import ServiceDetail from './pages/ServiceDetail';
import Projects from './pages/Projects';
import ProjectDetail from './pages/ProjectDetail';
import About from './pages/About';
import Contact from './pages/Contact';

// Admin Pages
import AdminLogin from './pages/admin/Login';
import AdminLayout from './pages/admin/Layout';
import AdminDashboard from './pages/admin/Dashboard';
import ProjectsManagement from './pages/admin/ProjectsManagement';
import ProjectForm from './pages/admin/ProjectForm';
import ServicesManagement from './pages/admin/ServicesManagement';
import PagesManagement from './pages/admin/PagesManagement';

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-gray-400">Yükleniyor...</div>
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/admin/login" replace />;
  }
  
  return children;
};

// Public Layout
const PublicLayout = ({ children }) => {
  return (
    <>
      <Navbar />
      <main>{children}</main>
      <Footer />
    </>
  );
};

function App() {
  return (
    <HelmetProvider>
      <Router>
        <AuthProvider>
          <LanguageProvider>
            <div className="min-h-screen bg-white text-bms-text" data-testid="app-container">
              <Routes>
                {/* Public Routes */}
                <Route path="/" element={
                  <PublicLayout>
                    <Home />
                  </PublicLayout>
                } />
                <Route path="/hizmetler" element={
                  <PublicLayout>
                    <Services />
                  </PublicLayout>
              } />
              <Route path="/hizmetler/:slug" element={
                <PublicLayout>
                  <ServiceDetail />
                </PublicLayout>
              } />
              <Route path="/projeler" element={
                <PublicLayout>
                  <Projects />
                </PublicLayout>
              } />
              <Route path="/projeler/:slug" element={
                <PublicLayout>
                  <ProjectDetail />
                </PublicLayout>
              } />
              <Route path="/hakkimizda" element={
                <PublicLayout>
                  <About />
                </PublicLayout>
              } />
              <Route path="/iletisim" element={
                <PublicLayout>
                  <Contact />
                </PublicLayout>
              } />
              
              {/* Admin Routes */}
              <Route path="/admin/login" element={<AdminLogin />} />
              <Route path="/admin" element={
                <ProtectedRoute>
                  <AdminLayout />
                </ProtectedRoute>
              }>
                <Route index element={<AdminDashboard />} />
                <Route path="projects" element={<ProjectsManagement />} />
                <Route path="projects/new" element={<ProjectForm />} />
                <Route path="projects/:id" element={<ProjectForm />} />
                <Route path="services" element={<ServicesManagement />} />
                <Route path="pages" element={<PagesManagement />} />
              </Route>
              
              {/* Catch all - redirect to home */}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </div>
        </LanguageProvider>
      </AuthProvider>
    </Router>
  </HelmetProvider>
  );
}

export default App;
